import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import KpiModifier from '../KpiCreate/KpiModifier';
import { useKpiModifiers, useModifierConfig } from '../../hooks/modifiers';
import { dateOptions } from '../../options';
import uuidv4 from '../../juristec-ui/utils/functions/randomUUID';
import Button from '../../juristec-ui/core/Button';

const origins = { filter: 'Filtros' };
const Body = styled.div`
  .gap-fix-first { display: flex; flex-direction: column; gap: 16px; }
  .gap-fix-second { display: flex; flex-wrap: wrap; gap: 16px; }
  .input-text-line-wrapper label.labelTxt {
    display: block;
    grid-template-columns: none;
    line-height: 1.2;
  }
  [role='alert'] { color: ${({ theme }) => theme.error}; }
`;

function FilterEditor({ value, onChange, campos, carregarValores, disabled }) {
  const [error, setError] = useState('');
  const dataFrameTypes = useMemo(() => Object.fromEntries(campos.map((campo) => {
    const type = campo.type || campo.tipo;
    const normalized = ({ numero: 'float64', float: 'float64', date: 'datetime64[ns]', texto: 'category' })[type] || type;
    return [campo.key, normalized];
  })), [campos]);

  const getUniqueValues = useCallback(async (column, selector) => {
    setError('');
    try {
      return await carregarValores(value.fonteId, column, selector);
    } catch (failure) {
      setError(failure.message || 'Não foi possível carregar os valores do campo.');
      return [];
    }
  }, [carregarValores, value.fonteId]);

  const config = useModifierConfig({ dataFrameTypes, modifierOrigins: origins, getUniqueValues });
  const actions = useKpiModifiers({
    dataFrameTypes,
    initialModifiers: {
      filter: (value.filtros ?? value.filters ?? []).map((filter) => ({
        ...filter,
        uid: filter.uid || uuidv4(),
        type: dataFrameTypes[filter.column],
        selector: filter.selector || '',
        inverse: filter.inverse ?? false,
      })),
    },
    onModifierChange: (next) => {
      const updated = { ...value };
      delete updated.filters;
      onChange({
        ...updated,
        filtros: next.filter.map(({ column, ftype, values, selector, inverse }) => ({
          column, ftype, values, selector, inverse,
        })),
      });
    },
  });

  return (
    <Body aria-disabled={disabled} style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
      <p>Estes filtros ficam salvos no explorador e são aplicados na ordem abaixo. Os filtros globais do painel são aplicados antes deles.</p>
      {error && <p role="alert">{error}</p>}
      <KpiModifier
        atModal
        modifiers={actions.modifiers}
        modifierType="filter"
        columnOptsMap={{ filter: campos.map((campo) => ({
          label: campo.label, value: campo.key, id: campo.key, type: dataFrameTypes[campo.key],
        })) }}
        getDateSelector={() => dateOptions[0]}
        modifierConfig={config.modifierConfig}
        simpleOptions={config.simpleOptions}
        originOptions={config.originOptions}
        getColumnOptions={config.getColumnOptions}
        getSimpleOptions={config.getSimpleOptions}
        handleModifierConfigs={config.handleModifierConfigs}
        configColumnIsDate={config.configColumnIsDate}
        getOptionsByConfigGroup={config.getOptionsByConfigGroup}
        checkModifierValues={config.checkModifierValues}
        modifierOrigins={origins}
        addModifier={() => {
          if (actions.addModifier(config.modifierConfig)) config.resetModifierConfigs();
        }}
        removeModifier={actions.removeModifier}
        editModifier={actions.editModifier}
        getUniqueValues={getUniqueValues}
        changeModifierOrder={actions.changeModifierOrder}
        dataFrameTypes={dataFrameTypes}
      />
    </Body>
  );
}

export default function ReportGridFilters({ value, onChange, carregarCampos, carregarValores, disabled = false }) {
  const [result, setResult] = useState({ source: '', campos: [], error: '', loading: false });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!value.fonteId) return undefined;
    let active = true;
    const source = value.fonteId;
    setResult({ source, campos: [], error: '', loading: true });
    Promise.resolve().then(() => carregarCampos(source))
      .then((campos) => { if (active) setResult({ source, campos, error: '', loading: false }); })
      .catch((failure) => {
        if (active) setResult({ source, campos: [], error: failure.message || 'Não foi possível carregar os campos.', loading: false });
      });
    return () => { active = false; };
  }, [value.fonteId, carregarCampos, retry]);

  if (!value.fonteId) return <p>Selecione uma fonte na aba Dados para configurar os filtros.</p>;
  if (result.source !== value.fonteId || result.loading) return <p role="status">Carregando campos…</p>;
  if (result.error) return <><p role="alert">{result.error}</p><Button type="button" disabled={disabled} onClick={() => setRetry((count) => count + 1)}>Tentar novamente</Button></>;
  return <FilterEditor key={value.fonteId} value={value} onChange={onChange} campos={result.campos} carregarValores={carregarValores} disabled={disabled} />;
}
